// src/components/myAccount/MyAccountSidebar.js
import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  User, 
  Key, 
  CreditCard, 
  Receipt,
  History,
  LogOut,
  ChevronDown,
  Building,
  FileText,
  HelpCircle,
  Users,
  DollarSign,
  BarChart3,
  Settings
} from 'lucide-react';
import { supabase } from '../../config';

const MyAccountSidebar = ({ userProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Product menu items
  const productMenuItems = [
    {
      id: 'equity',
      name: 'Equity',
      description: 'Manage companies, shareholders, and equity',
      icon: Building,
      path: '/equity',
      color: 'blue'
    },
    {
      id: 'dataroom',
      name: 'Dealroom',
      description: 'Secure document sharing and collaboration',
      icon: FileText,
      path: '/dealroom',
      color: 'green'
    },
    {
      id: 'capraise',
      name: 'CapRaise',
      description: 'Capital raising and investor management',
      icon: DollarSign,
      path: '/capraise',
      color: 'purple'
    },
    {
      id: 'questions',
      name: 'Questions',
      description: 'Support and knowledge management',
      icon: HelpCircle,
      path: '/questions',
      color: 'orange'
    },
    {
      id: 'userinvites',
      name: 'User Invites',
      description: 'Send invitations to team members',
      icon: Users,
      path: '/userinvites',
      color: 'indigo'
    },
    {
      id: 'marketData',
      name: 'MarketData',
      description: 'Financial data, research, and analytics',
      icon: BarChart3,
      path: '/market-data',
      color: 'teal'
    }
  ];

  const handleProductClick = (productId) => {
    const product = productMenuItems.find(item => item.id === productId);
    if (product) {
      navigate(product.path);
      setIsProductMenuOpen(false);
    }
  };

  const handleAdminClick = () => {
    navigate('/adminhq');
    setIsProductMenuOpen(false);
  };

  const navItems = [
    {
      label: 'Dashboard',
      path: '/my-account/dashboard',
      icon: LayoutDashboard,
    },
    {
      label: 'Profile',
      path: '/my-account/profile',
      icon: User,
    },
    {
      label: 'Login Details',
      path: '/my-account/login-details',
      icon: Key,
    },
    {
      label: 'Subscriptions',
      path: '/my-account/subscriptions',
      icon: Receipt,
    },
    {
      label: 'Payment Methods',
      path: '/my-account/payment-methods',
      icon: CreditCard,
    },
    {
      label: 'Billing History',
      path: '/my-account/billing-history',
      icon: History,
    },
  ];

  const getLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
        : 'text-gray-700 hover:bg-gray-100'
    }`;
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* Header with Logo/Title */}
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-gray-900">My Account</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your profile and subscriptions</p>
      </div>

      {/* Product Dropdown - Hard left, top of navbar column under logo */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <button
            onClick={() => setIsProductMenuOpen(!isProductMenuOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Settings className="w-4 h-4" />
              <span className="truncate">Products</span>
            </div>
            <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isProductMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Product Dropdown Menu */}
          {isProductMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsProductMenuOpen(false)}
              />
              <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-2">
                  <div className="space-y-1">
                    {productMenuItems.map((product) => {
                      const Icon = product.icon;
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleProductClick(product.id)}
                          className="w-full flex items-center space-x-3 p-2 rounded-lg text-left transition-colors hover:bg-gray-50"
                        >
                          <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-gray-100">
                            <Icon className="w-3 h-3 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {product.name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {/* AdminHQ for Super Admins */}
                  {userProfile?.is_super_admin && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={handleAdminClick}
                        className="w-full flex items-center space-x-2 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors"
                      >
                        <Settings className="w-3 h-3" />
                        <span>Admin HQ</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={getLinkClasses(item.path)}
          >
            <item.icon className="h-5 w-5 mr-3" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <LogOut className="h-5 w-5 mr-3" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default MyAccountSidebar;